/**
 * Shared context for the Interactive Strategy Canvas.
 *
 * Provides template, instance, and section data to all mode views
 * without modifying CanvasExplorer's internal state management.
 */

import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from 'react'
import {
  CANVAS_TEMPLATES,
  BUSINESS_MODEL_CANVAS,
  type CanvasTemplate,
} from '@/components/canvas/canvasTemplates'
import {
  useCanvasInstances,
  useCanvasSectionData,
  useEnsureDefaultCanvas,
} from '@/hooks/useCanvasInstances'
import { useConnectionStore } from '@/stores/connectionStore'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useAIStore } from '@/stores/aiStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { getInteractiveConfig, type SectionStats, type SectionItem } from '@ispot/shared'
import type { CanvasSectionData, LinkedCanvasItem } from '@/types/canvas'

export type InteractiveMode = 'canvas' | 'connections' | 'guided' | 'challenges' | 'story'

interface CanvasInteractiveContextValue {
  // Template & instance
  selectedTemplate: CanvasTemplate
  setSelectedTemplate: (t: CanvasTemplate) => void
  selectedInstanceUri: string | null
  setSelectedInstanceUri: (uri: string | null) => void
  instances: Array<{ uri: string; name: string }>
  instancesLoading: boolean

  // Section data
  sectionData: CanvasSectionData[]
  sectionStats: Record<string, SectionStats>
  sectionItems: Record<string, SectionItem[]>
  canvasContext: Record<string, string[]>

  // Interactive config
  interactiveConfig: ReturnType<typeof getInteractiveConfig>
  hasInteractiveConfig: boolean

  // Mode
  activeMode: InteractiveMode
  setActiveMode: (mode: InteractiveMode) => void

  // Guided mode navigation from challenges
  guidedTargetSection: string | null
  navigateToGuidedSection: (sectionId: string) => void
  clearGuidedTarget: () => void

  // Auth context for API calls
  accountId: string | undefined
  projectId: string | undefined
  apiKey: string | null
}

const Context = createContext<CanvasInteractiveContextValue | null>(null)

export function useCanvasInteractive() {
  const ctx = useContext(Context)
  if (!ctx) throw new Error('useCanvasInteractive must be used within CanvasInteractiveProvider')
  return ctx
}

interface ProviderProps {
  children: ReactNode
}

export function CanvasInteractiveProvider({ children }: ProviderProps) {
  const { isConnected } = useConnectionStore()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const { apiKey } = useAIStore()

  const [selectedTemplate, setSelectedTemplate] = useState<CanvasTemplate>(BUSINESS_MODEL_CANVAS)
  const [selectedInstanceUri, setSelectedInstanceUri] = useState<string | null>(null)
  const [activeMode, setActiveMode] = useState<InteractiveMode>('canvas')
  const [guidedTargetSection, setGuidedTargetSection] = useState<string | null>(null)

  // Fetch instances for selected template
  const { data: instances = [], isLoading: instancesLoading } = useCanvasInstances(
    selectedTemplate.id,
  )

  // Auto-select first instance
  const effectiveInstanceUri = useMemo(() => {
    if (selectedInstanceUri && instances.find((i) => i.uri === selectedInstanceUri)) {
      return selectedInstanceUri
    }
    return instances.length > 0 ? instances[0].uri : null
  }, [selectedInstanceUri, instances])

  // Fetch section data for active instance
  const { data: sectionData = [] } = useCanvasSectionData(effectiveInstanceUri)
  const setActiveResource = useSkerpaContextStore(s => s.setActiveResource)

  useEffect(() => {
    setActiveResource(effectiveInstanceUri)
    return () => setActiveResource(null)
  }, [effectiveInstanceUri, setActiveResource])

  // Build section stats for challenge engine
  const sectionStats = useMemo(() => {
    const stats: Record<string, SectionStats> = {}
    for (const section of sectionData) {
      stats[section.sectionId] = {
        count: (section.linkedItems?.length || 0),
        hasNotes: Boolean(section.notes?.trim()),
      }
    }
    return stats
  }, [sectionData])

  // Build section items for placeholder resolution
  const sectionItems = useMemo(() => {
    const items: Record<string, SectionItem[]> = {}
    for (const section of sectionData) {
      items[section.sectionId] = (section.linkedItems || []).map((item) => ({
        label: item.label || item.note || 'Unnamed',
      }))
    }
    return items
  }, [sectionData])

  // Build canvas context (string arrays per section for AI)
  const canvasContext = useMemo(() => {
    const context: Record<string, string[]> = {}
    for (const section of sectionData) {
      const items: string[] = []
      if (section.notes?.trim()) items.push(section.notes)
      for (const item of section.linkedItems || []) {
        if (item.label) items.push(item.label)
        if (item.note) items.push(item.note)
      }
      if (items.length > 0) context[section.sectionId] = items
    }
    return context
  }, [sectionData])

  // Interactive config for current template
  const interactiveConfig = useMemo(
    () => getInteractiveConfig(selectedTemplate.id),
    [selectedTemplate.id],
  )
  const hasInteractiveConfig = Boolean(interactiveConfig.guidedQuestions)

  const navigateToGuidedSection = (sectionId: string) => {
    setGuidedTargetSection(sectionId)
    setActiveMode('guided')
  }

  const clearGuidedTarget = () => setGuidedTargetSection(null)

  const value = useMemo<CanvasInteractiveContextValue>(
    () => ({
      selectedTemplate,
      setSelectedTemplate,
      selectedInstanceUri: effectiveInstanceUri,
      setSelectedInstanceUri,
      instances,
      instancesLoading,
      sectionData,
      sectionStats,
      sectionItems,
      canvasContext,
      interactiveConfig,
      hasInteractiveConfig,
      activeMode,
      setActiveMode,
      guidedTargetSection,
      navigateToGuidedSection,
      clearGuidedTarget,
      accountId: currentAccount?.id,
      projectId: currentProject?.id,
      apiKey,
    }),
    [
      selectedTemplate,
      effectiveInstanceUri,
      instances,
      instancesLoading,
      sectionData,
      sectionStats,
      sectionItems,
      canvasContext,
      interactiveConfig,
      hasInteractiveConfig,
      activeMode,
      guidedTargetSection,
      currentAccount?.id,
      currentProject?.id,
      apiKey,
    ],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
